import React from 'react'
import styled from 'styled-components'

import CarouselCTACard from '../Bookmark/Carousels/CarouselCTACard'
import OfferItemsCarousel from '../Bookmark/Carousels/OfferItemCarousel'
import SearchTourOrCruise from '../TripModal/TripItems/Search/SearchTourOrCruise'
import TourCTA from '../TripSummary/CallToActionButtons/TourCTA'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useOfferList } from 'hooks/Offers/useOfferList'
import useToggle from 'hooks/useToggle'
import { useLEPlaceByCoordinates } from 'tripPlanner/hooks/api'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { LETripItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

const NotFound = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

type Props = {
  lat: number
  lng: number
  className?: string
  item: LETripItem
}

const TYPES: Array<Tours.V1OfferType | Tours.V2ParentOfferType> = [
  'tour',
  'tour_v2',
]

const ViewNearbyToursCta = ({ lat, lng, item, className }: Props) => {
  const openModal = useOpenModal()
  const { value: modalVisible, on: showModal, off: hideModal } = useToggle()
  const { data: place, isLoading: fetchingPlace } = useLEPlaceByCoordinates(
    lat,
    lng,
  )

  const [offerList] = useOfferList(
    {
      destinationId: place?.id,
      searchNearby: true,
      offerTypes: TYPES,
      frontPageVisibleOnly: true,
    },
    { disabled: fetchingPlace },
  )

  const isLoading = fetchingPlace || offerList.fetching

  return (
    <>
      <Modal
        isOpen={modalVisible}
        title="Recommended Offers"
        onClose={hideModal}
        size="large"
      >
        <VerticalSpacer gap={16}>
          {isLoading && (
            <>
              <BodyText
                align="center"
                colour="neutral-one"
                variant="large"
                weight="bold"
              >
                Finding the best offers for you
              </BodyText>
              <LoadingIndicator />
            </>
          )}
          {!isLoading && offerList.offerIds.length === 0 && (
            <NotFound gap={16}>
              <BodyText
                align="center"
                colour="neutral-one"
                variant="large"
                weight="bold"
              >
                Sorry, we can't find anything nearby
              </BodyText>
              <TextButton
                kind="primary"
                fit="auto"
                onClick={() => {
                  hideModal()
                  openModal(SearchTourOrCruise, {
                    tripId: item.tripId,
                  })
                }}
              >
                Search all Tours & Cruises
              </TextButton>
            </NotFound>
          )}
          {!isLoading && offerList.offerIds.length > 0 && (
            <OfferItemsCarousel
              offerIds={offerList.offerIds.slice(0, 4)}
              tripId={item.tripId}
              sectionId={itemSectionId(item.id)}
              ctaCard={
                <CarouselCTACard>
                  <TourCTA
                    key="ctaCard"
                    tripId={item.tripId}
                    beforeClick={hideModal}
                  />
                </CarouselCTACard>
              }
            />
          )}
        </VerticalSpacer>
      </Modal>
      <TextButton
        kind="secondary"
        size="medium"
        className={className}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          showModal()
        }}
      >
        View more offers
      </TextButton>
    </>
  )
}

export default ViewNearbyToursCta
